import * as breakpoints from "../breakpoints";
import * as colors from "../constants/colors";
import * as languages from "../constants/languages";
import browserHistory from "react-router/lib/browserHistory";
import { css, StyleSheet } from "aphrodite";
import { english, spanish } from "../constants/strings";
import { drupalSiteUrl, rootUrl } from "../config.js";
import { handleUnknownError } from "../lib/errorHandler";
import { logout } from "../api/auth";
import globalStyles from "../styles/globalStyles";
import Link from "react-router/lib/Link";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

const styles = StyleSheet.create({
	container: {
		"@media print": {
			display: "none",
		},
	},
	dividerSmall: {
		width: 1,
		height: 16,
		backgroundColor: colors.white,
		[breakpoints.mobileOnly]: {
			width: 3,
			height: "100%",
			position: "absolute",
			top: 0,
			right: 0,
		},
	},
	dividerLarge: {
		width: 1,
		[breakpoints.tabletAndUp]: {
			backgroundColor: colors.gray,
			height: 22,
		},
		[breakpoints.mobileOnly]: {
			backgroundColor: colors.white,
			height: 23,
		},
	},
	exitButton: {
		backgroundColor: "transparent",
		border: "none",
		padding: 0,
		margin: 0,
	},
	languageButtonContainer: {
		paddingRight: 20,
	},
	languageButton: {
		backgroundColor: "transparent",
		border: "none",
		color: colors.teal,
		fontFamily: "priori-sans",
		fontSize: 14,
		fontWeight: "bold",
	},
	left: {
		display: "flex",
		alignItems: "center",
		flex: 1,
	},
	logoPadding: {
		maxWidth: 78,
		width: "calc(50vw - 480px)",
	},
	logoContainer: {
		paddingTop: 12,
		paddingBottom: 8,
		[breakpoints.mobileOnly]: {
			maxWidth: 172,
		},
		display: "flex",
		justifyContent: "flex-end",
		paddingLeft: 10,
		":before": {
			content: "''",
			display: "inline-block",
			backgroundColor: colors.white,
			maxWidth: 71,
			width: "calc(50vw - 500px)",
		},
	},
	logoContainerMobile: {
		color: colors.teal,
		paddingLeft: 16,
		paddingTop: 16,
		paddingBottom: 12,
		fontSize: 12,
		letterSpacing: 0.5,
		lineHeight: 1.08,
	},
	navContainer: {
		backgroundColor: colors.teal,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		paddingRight: 6,
		paddingLeft: 10,
	},
	tabActive: {
		color: colors.black,
		backgroundColor: colors.tealHighlight,
		[breakpoints.mobileOnly]: {
			backgroundColor: colors.lightTealHighlight,
		},
	},
	tabInactive: {
		color: colors.white,
		[breakpoints.mobileOnly]: {
			color: colors.black,
		},
		[breakpoints.desktopOnly]: {
			":hover": {
				backgroundColor: colors.tealHighlight,
				color: colors.black,
			},
		},
	},
	tabContainer: {
		display: "flex",
		alignItems: "center",
	},
	tabContainerLeft: {
		flexGrow: 1,
		[breakpoints.mobileOnly]: {
			alignItems: "stretch",
			backgroundColor: colors.lightTeal,
		},
	},
	tabLeft: {
		paddingTop: 30,
		paddingBottom: 24,
		paddingLeft: 8,
		fontSize: 15,
		display: "flex",
		fontFamily: "priori-sans",
		fontWeight: "bold",
		textDecoration: "none",
		[breakpoints.mobileOnly]: {
			flex: 1,
			justifyContent: "center",
			alignItems: "center",
			paddingTop: 16,
			paddingBottom: 16,
			lineHeight: 1.75,
			fontSize: 12,
			position: "relative",
			fontFamily:
				"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
			fontWeight: "bold",
		},
		[breakpoints.desktopOnly]: {
			":hover": {
				cursor: "pointer",
			},
		},
	},
	tabLeftText: {
		marginRight: 8,
		textAlign: "center",
	},
	tabRight: {
		color: colors.white,
		border: "none",
		backgroundColor: "transparent",
		fontSize: 15,
		lineHeight: 1.4,
		paddingTop: 24,
		paddingBottom: 24,
		paddingRight: 32,
		paddingLeft: 32,
		textDecoration: "underline",
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		[breakpoints.mobileOnly]: {
			lineHeight: 1.75,
			fontSize: 12,
			paddingRight: 16,
			paddingLeft: 16,
		},
		[breakpoints.desktopOnly]: {
			":hover": {
				cursor: "pointer",
			},
		},
	},
	topHeader: {
		backgroundColor: colors.white,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		[breakpoints.mobileOnly]: {
			backgroundColor: colors.gray3,
			paddingLeft: 16,
		},
	},
	topLogoContainer: {
		flex: 1,
	},
	wicLogo: {
		height: 51,
		width: 132,
		[breakpoints.smallMobileOnly]: {
			height: 45,
		},
	},
	wicLogoContainerLoggedIn: {
		paddingTop: 8,
		paddingBottom: 8,
		[breakpoints.mobileOnly]: {
			paddingLeft: 6,
			paddingTop: 12,
			paddingBottom: 10,
		},
		":before": {
			content: "''",
			display: "inline-block",
			maxWidth: 71,
			width: "calc(50vw - 500px)",
		},
		":after": {
			content: "''",
			display: "inline-block",
			maxWidth: 71,
			minWidth: 24,
			width: "calc(50vw - 500px)",
		},
	},
	wicLogoContainerLoggedOut: {
		paddingTop: 8,
		paddingBottom: 8,
		[breakpoints.mobileOnly]: {
			paddingLeft: 18,
			paddingTop: 12,
			paddingBottom: 10,
		},
		":before": {
			content: "''",
			display: "inline-block",
			maxWidth: 71,
			width: "calc(50vw - 500px)",
		},
		":after": {
			content: "''",
			display: "inline-block",
			maxWidth: 71,
			minWidth: 24,
			width: "calc(50vw - 500px)",
		},
	},
});

export default class Header extends PureComponent {
	static propTypes = {
		activeTab: PropTypes.string,
		language: PropTypes.string.isRequired,
		currentRoute: PropTypes.string.isRequired,
		loggedIn: PropTypes.bool,
		profileClicked: PropTypes.func,
		inApp: PropTypes.bool,
	};

	static defaultProps = {
		loggedIn: true,
		inApp: true,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;

		this.tabs = [
			{
				label: this.strings.tabNames.MY_CLASSES,
				path: "/my-classes",
			},
			{
				label: this.strings.tabNames.CATALOG,
				path: "/catalog",
			},
			{
				label: this.strings.tabNames.FAQ,
				path: "/faq",
			},
		];
	}

	_exit = () => {
		const url = `${drupalSiteUrl}${this.props.language === languages.es ? "/es" : ""}`;

		window.location.assign(url);
	};

	_logout = () => {
		// eslint-disable-next-line
		if (confirm(this.strings.logoutPrompt)) {
			logout()
				.then(() => {
					const url =
						this.props.language === languages.es
							? `${drupalSiteUrl}/es/clases-en-linea`
							: `${drupalSiteUrl}/online-classes`;

					window.location.assign(url);
				})
				.catch(err => {
					handleUnknownError(err);
				});
		}
	};

	_toggleLanguage = () => {
		if (this.props.language === languages.es) {
			// redirect to english route
			browserHistory.push(
				`${this.props.currentRoute}${
					this.props.currentRoute.indexOf("?") > -1 ? "&" : "?"
				}language=en`,
			);
			window.location.reload();
		} else {
			// redirect to spanish route
			browserHistory.push(
				`${this.props.currentRoute}${
					this.props.currentRoute.indexOf("?") > -1 ? "&" : "?"
				}language=es`,
			);
			window.location.reload();
		}
	};

	_renderTopLogo = () => {
		return (
			<div className={css(styles.topHeader)}>
				<div className={css(styles.left)}>
					<div className={css([styles.logoContainer, globalStyles.tabletAndUp])}>
						<button onClick={this._exit} className={css(styles.exitButton)}>
							<img
								width="154"
								alt="Texas Health and Human Services"
								src={`${rootUrl}/images/TxHHS.png`}
								srcSet={`${rootUrl}/images/TxHHS@2x.png 2x, ${rootUrl}/images/TxHHS@3x.png 3x`}
							/>
						</button>
					</div>
					<div className={css(globalStyles.mobileOnly)}>
						{this.props.inApp ? (
							<div
								className={css([
									styles.logoContainerMobile,
									globalStyles.mobileOnly,
								])}
							>
								{this.strings.thhs}
							</div>
						) : (
							<div className={css(styles.logoContainer)}>
								<button onClick={this._exit} className={css(styles.exitButton)}>
									<img
										width="154"
										alt="Texas Health and Human Services"
										src={`${rootUrl}/images/TxHHS.png`}
										srcSet={`${rootUrl}/images/TxHHS@2x.png 2x, ${rootUrl}/images/TxHHS@3x.png 3x`}
									/>
								</button>
							</div>
						)}
					</div>
				</div>
				<div className={css(styles.languageButtonContainer)}>
					<button className={css(styles.languageButton)} onClick={this._toggleLanguage}>
						{this.strings.languageToggle}
					</button>
				</div>
			</div>
		);
	};

	_renderTabs = () => {
		return (
			<div className={css([styles.tabContainer, styles.tabContainerLeft])}>
				{this.tabs.map((tab, i) => (
					<Link
						key={tab.label}
						to={{ pathname: tab.path, query: { language: this.props.language } }}
						className={css([
							styles.tabLeft,
							tab.label === this.props.activeTab
								? styles.tabActive
								: styles.tabInactive,
						])}
					>
						<div className={css(styles.tabLeftText)}>{tab.label}</div>
						{i < this.tabs.length - 1 ? (
							<div className={css(styles.dividerSmall)} />
						) : null}
					</Link>
				))}
			</div>
		);
	};

	render() {
		return (
			<header className={css(styles.container)}>
				{this._renderTopLogo()}
				<nav className={css(styles.navContainer)}>
					<div className={css(styles.left)}>
						<div
							className={css(
								this.props.loggedIn
									? styles.wicLogoContainerLoggedIn
									: styles.wicLogoContainerLoggedOut,
							)}
						>
							<button onClick={this._exit} className={css(styles.exitButton)}>
								<img
									alt="Texas WIC logo"
									className={css(styles.wicLogo)}
									// height="51"
									src={`${rootUrl}/images/texas-wic-logo-reverse.svg`}
								/>
							</button>
						</div>
						<div className={css(styles.wicLogoPaddingRight)} />
						{this.props.inApp ? (
							<div className={css(globalStyles.tabletAndUp)}>
								{this._renderTabs()}
							</div>
						) : null}
					</div>
					{this.props.loggedIn ? (
						<div className={css(styles.tabContainer)}>
							<button
								className={css(styles.tabRight)}
								onClick={this.props.profileClicked}
							>
								{this.strings.PROFILE}
							</button>
							<div className={css(styles.dividerLarge)} />
							<button className={css(styles.tabRight)} onClick={this._logout}>
								{this.strings.LOG_OUT}
							</button>
						</div>
					) : /* <button className={css(styles.exitButton)} onClick={this._exit}>
							<img
								height="51"
								width="87"
								alt="Exit"
								src={`${rootUrl}/images/exit.png`}
							/>
						</button> */
					null}
				</nav>
				{this.props.inApp ? (
					<div className={css(globalStyles.mobileOnly)}>{this._renderTabs()}</div>
				) : null}
			</header>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Header.js